import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import PageTabBar from '../components/PageTabBar';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Printer, Settings, RotateCcw, Save, Columns, Rows, ChevronDown, ChevronUp } from 'lucide-react';
import { STOCK_TABS, ETIQUETTES_SUB_TABS } from './_stockTabs';

const DEFAULT_CONFIG = {
  label_width: 38.1,
  label_height: 21.2,
  cols: 5,
  rows: 13,
  left_margin: 4.7,
  top_margin: 10.7,
  h_gap: 1.3,
  v_gap: 0.0,
  col_offsets: [0, 0, 0, 0, 0],
  row_offsets: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
};

const NumberInput = ({ label, value, onChange, step = 0.1, unit = "mm", min, testId }) => (
  <div>
    <label className="block text-xs text-gray-400 mb-1">{label}</label>
    <div className="flex items-center gap-1">
      <input
        type="number"
        step={step}
        min={min}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="input-dark w-full py-1.5 text-sm"
        data-testid={testId}
      />
      <span className="text-xs text-gray-500 w-6">{unit}</span>
    </div>
  </div>
);

const LabelConfigPage = () => {
  const { api } = useAuth();
  const { t, formatNumber } = useLanguage();
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [saved, setSaved] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showColOffsets, setShowColOffsets] = useState(false);
  const [showRowOffsets, setShowRowOffsets] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await api.get('/stock/label-config');
      setConfig({ ...DEFAULT_CONFIG, ...res.data });
    } catch {
      setConfig(DEFAULT_CONFIG);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const updateField = (field, value) => {
    setConfig(prev => {
      const next = { ...prev, [field]: value };
      // Adjust offset arrays when cols/rows change
      if (field === 'cols') {
        const n = Math.max(1, Math.min(10, parseInt(value) || 1));
        next.cols = n;
        const arr = [...(prev.col_offsets || [])];
        while (arr.length < n) arr.push(0);
        next.col_offsets = arr.slice(0, n);
      }
      if (field === 'rows') {
        const n = Math.max(1, Math.min(30, parseInt(value) || 1));
        next.rows = n;
        const arr = [...(prev.row_offsets || [])];
        while (arr.length < n) arr.push(0);
        next.row_offsets = arr.slice(0, n);
      }
      return next;
    });
    setSaved(false);
  };

  const updateColOffset = (idx, val) => {
    setConfig(prev => {
      const arr = [...prev.col_offsets];
      arr[idx] = val;
      return { ...prev, col_offsets: arr };
    });
    setSaved(false);
  };

  const updateRowOffset = (idx, val) => {
    setConfig(prev => {
      const arr = [...prev.row_offsets];
      arr[idx] = val;
      return { ...prev, row_offsets: arr };
    });
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/stock/label-config', config);
      setSaved(true);
    } catch (err) {
      alert(t('labelConfig.save_error', { error: err.response?.data?.detail || err.message }));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setConfig({
      ...DEFAULT_CONFIG,
      col_offsets: Array(DEFAULT_CONFIG.cols).fill(0),
      row_offsets: Array(DEFAULT_CONFIG.rows).fill(0),
    });
    setSaved(false);
  };

  // A4 preview dimensions (scaled) 
  const PREVIEW_W = 320;
  const PREVIEW_H = PREVIEW_W * (297 / 210);
  const scale = PREVIEW_W / 210; // mm to px

  const previewLabels = useMemo(() => {
    const labels = [];
    const { cols, rows, label_width, label_height, left_margin, top_margin, h_gap, v_gap, col_offsets, row_offsets } = config;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = left_margin + c * (label_width + h_gap) + (col_offsets[c] || 0);
        const y = top_margin + r * (label_height + v_gap) - (row_offsets[r] || 0);
        labels.push({ x: x * scale, y: y * scale, w: label_width * scale, h: label_height * scale, col: c, row: r });
      }
    }
    return labels;
  }, [config, scale]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto fade-in" data-testid="label-config-page">
        <PageTabBar tabs={STOCK_TABS} activeColor="rose" />
        <PageTabBar tabs={ETIQUETTES_SUB_TABS} activeColor="emerald" />
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-amber-500/10 text-amber-400">
              <Settings className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-white">{t('labelConfig.title')}</h2>
              <p className="text-sm text-gray-400">{t('labelConfig.subtitle')}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-gray-300 text-sm font-medium border border-white/10 transition-all"
              data-testid="label-config-reset"
            >
              <RotateCcw className="w-4 h-4" /> {t('labelConfig.reset')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving || saved}
              className={`flex items-center gap-2 px-5 py-2 rounded-xl font-semibold text-sm transition-all ${
                saved
                  ? 'bg-gray-700 text-gray-500 border border-white/5'
                  : 'bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/30'
              }`}
              data-testid="label-config-save"
            >
              <Save className="w-4 h-4" /> {saving ? t('labelConfig.saving') : saved ? t('labelConfig.saved') : t('labelConfig.save')}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_1fr] gap-6">
          {/* Settings Panel */}
          <div className="space-y-4">
            {/* Global dimensions */}
            <div className="glass p-5">
              <h3 className="text-base font-bold text-white mb-4">{t('labelConfig.global_dimensions')}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <NumberInput label={t('labelConfig.label_width')} value={config.label_width} onChange={v => updateField('label_width', v)} testId="cfg-label-width" />
                <NumberInput label={t('labelConfig.label_height')} value={config.label_height} onChange={v => updateField('label_height', v)} testId="cfg-label-height" />
                <NumberInput label={t('labelConfig.cols')} value={config.cols} onChange={v => updateField('cols', v)} step={1} unit="" min={1} testId="cfg-cols" />
                <NumberInput label={t('labelConfig.rows')} value={config.rows} onChange={v => updateField('rows', v)} step={1} unit="" min={1} testId="cfg-rows" />
              </div>
            </div>

            {/* Margins */}
            <div className="glass p-5">
              <h3 className="text-base font-bold text-white mb-4">{t('labelConfig.margins_spacing')}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <NumberInput label={t('labelConfig.left_margin')} value={config.left_margin} onChange={v => updateField('left_margin', v)} testId="cfg-left-margin" />
                <NumberInput label={t('labelConfig.top_margin')} value={config.top_margin} onChange={v => updateField('top_margin', v)} testId="cfg-top-margin" />
                <NumberInput label={t('labelConfig.h_spacing')} value={config.h_gap} onChange={v => updateField('h_gap', v)} testId="cfg-h-gap" />
                <NumberInput label={t('labelConfig.v_spacing')} value={config.v_gap} onChange={v => updateField('v_gap', v)} testId="cfg-v-gap" />
              </div>
            </div>

            {/* Per-column offsets */}
            <div className="glass p-5">
              <button
                onClick={() => setShowColOffsets(!showColOffsets)}
                className="flex items-center justify-between w-full text-left"
                data-testid="toggle-col-offsets"
              >
                <div className="flex items-center gap-2">
                  <Columns className="w-4 h-4 text-blue-400" />
                  <h3 className="text-base font-bold text-white">{t('labelConfig.col_offset_title')}</h3>
                  <span className="text-xs text-gray-500">{t('labelConfig.cols_count', { count: formatNumber(config.cols) })}</span>
                </div>
                {showColOffsets ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>
              {showColOffsets && (
                <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-3">
                  {(config.col_offsets || []).map((val, i) => (
                    <NumberInput
                      key={`col-${i}`}
                      label={t('labelConfig.col_n', { n: i + 1 })}
                      value={val}
                      onChange={v => updateColOffset(i, v)}
                      testId={`cfg-col-offset-${i}`}
                    />
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">{t('labelConfig.col_offset_hint')}</p>
            </div>

            {/* Per-row offsets */}
            <div className="glass p-5">
              <button
                onClick={() => setShowRowOffsets(!showRowOffsets)}
                className="flex items-center justify-between w-full text-left"
                data-testid="toggle-row-offsets"
              >
                <div className="flex items-center gap-2">
                  <Rows className="w-4 h-4 text-violet-400" />
                  <h3 className="text-base font-bold text-white">{t('labelConfig.row_offset_title')}</h3>
                  <span className="text-xs text-gray-500">{t('labelConfig.rows_count', { count: formatNumber(config.rows) })}</span>
                </div>
                {showRowOffsets ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>
              {showRowOffsets && (
                <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-3">
                  {(config.row_offsets || []).map((val, i) => (
                    <NumberInput
                      key={`row-${i}`}
                      label={t('labelConfig.row_n', { n: i + 1 })}
                      value={val}
                      onChange={v => updateRowOffset(i, v)}
                      testId={`cfg-row-offset-${i}`}
                    />
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">{t('labelConfig.row_offset_hint')}</p>
            </div>
          </div>

          {/* A4 Preview */}
          <div className="sticky self-start" style={{ top: '5rem' }}>
            <div className="glass p-5">
              <h3 className="text-base font-bold text-white mb-4">{t('labelConfig.preview_title')}</h3>
              <div
                className="relative bg-white rounded-lg mx-auto overflow-hidden"
                style={{ width: PREVIEW_W, height: PREVIEW_H }}
                data-testid="label-preview-a4"
              >
                {previewLabels.map((lbl, i) => (
                  <div
                    key={i}
                    className="absolute border border-gray-300 flex items-center justify-center text-[6px] text-gray-500 overflow-hidden"
                    style={{
                      left: lbl.x,
                      top: lbl.y,
                      width: lbl.w,
                      height: lbl.h,
                      backgroundColor: i === 0 ? '#dbeafe' : '#f9fafb',
                    }}
                  >
                    {i < 10 && <span className="text-[7px] text-gray-600 font-medium">{lbl.col + 1},{lbl.row + 1}</span>}
                  </div>
                ))}
              </div>
              <div className="mt-3 text-xs text-gray-500 text-center">
                {t('labelConfig.preview_count', { cols: formatNumber(config.cols), rows: formatNumber(config.rows), total: formatNumber(config.cols * config.rows) })}
                <span className="mx-2">|</span>
                {t('labelConfig.preview_dims', { width: formatNumber(config.label_width), height: formatNumber(config.label_height) })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default LabelConfigPage;
